import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { previewPaymentNotifications, regenerateUserPaymentMessage, regenerateAllPaymentMessages, sendPaymentNotifications } from '../../adminApi';
import styles from './PaymentMessageReviewModal.module.css';

const PaymentMessageReviewModal = ({ 
  isOpen, 
  onClose, 
  season,
  useLLM: useLLMProp,
  onSendComplete 
}) => {
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regeneratingUserId, setRegeneratingUserId] = useState(null);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Track the last useLLM value to detect changes
  const prevUseLLMRef = React.useRef(useLLMProp);

  const loadPreview = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await previewPaymentNotifications(season, useLLMProp);
      setPreviewData(response.data);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [season, useLLMProp]);

  // Load preview when modal opens (only if no data) or when useLLM changes
  React.useEffect(() => {
    if (isOpen) {
      const useLLMChanged = prevUseLLMRef.current !== useLLMProp;
      if (!previewData || useLLMChanged) {
        // Only load if we don't have data, or if LLM setting changed
        if (useLLMChanged) {
          setPreviewData(null); // Clear existing data when LLM setting changes
          prevUseLLMRef.current = useLLMProp;
        }
        loadPreview();
      }
    }
  }, [isOpen, useLLMProp, previewData, loadPreview]);

  // Reset error when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleRegenerateUser = async (userId) => {
    try {
      setRegeneratingUserId(userId);
      setError(null);
      const response = await regenerateUserPaymentMessage(season, userId, useLLMProp);
      const newMessage = response.data.message;
      
      // Update the specific user's message in previewData
      setPreviewData(prev => ({
        ...prev,
        userMessages: prev.userMessages.map(msg => 
          msg.user_id === userId ? newMessage : msg
        )
      }));
    } catch (err) {
      console.error('Error regenerating user message:', err);
      setError(err.response?.data?.message || 'Failed to regenerate message');
    } finally {
      setRegeneratingUserId(null);
    }
  };

  const handleRegenerateAll = async () => {
    try {
      setRegeneratingAll(true);
      setError(null);
      const response = await regenerateAllPaymentMessages(season, useLLMProp);
      setPreviewData(response.data);
    } catch (err) {
      console.error('Error regenerating all messages:', err);
      setError(err.response?.data?.message || 'Failed to regenerate messages');
    } finally {
      setRegeneratingAll(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(`Send ${previewData.userMessages.length} payment notification(s) to Discord?`)) {
      return;
    }

    try {
      setSending(true);
      setError(null);
      const response = await sendPaymentNotifications(season, useLLMProp);
      setSending(false);
      onSendComplete(response.data);
      onClose();
    } catch (err) {
      console.error('Error sending notifications:', err);
      setError(err.response?.data?.message || 'Failed to send notifications');
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>📧 Review Payment Notifications</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.body}>
          {error && (
            <div className={styles.errorMessage}>
              ❌ {error}
            </div>
          )}

          {loading && (
            <div className={styles.loadingMessage}>
              Loading preview...
            </div>
          )}

          {previewData && !loading && (
            <>
              {/* Header Message */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h3>💰 Payment Methods Header</h3>
                  <button
                    className={styles.refreshButton}
                    onClick={loadPreview}
                    disabled={loading}
                    title="Refresh preview data"
                  >
                    {loading ? '⏳ Loading...' : '🔄 Refresh'}
                  </button>
                </div>
                <div className={styles.messageBox}>
                  <pre className={styles.messageText}>{previewData.headerMessage || 'No header message'}</pre>
                </div>
              </div>

              {/* User Messages */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h3>Individual Messages ({previewData.userMessages?.length || 0})</h3>
                  <button
                    className={styles.regenerateAllButton}
                    onClick={handleRegenerateAll}
                    disabled={regeneratingAll}
                  >
                    {regeneratingAll ? '🔄 Regenerating...' : '🔄 Regenerate All'}
                  </button>
                </div>

                <div className={styles.messagesList}>
                  {previewData.userMessages?.map((userMsg, index) => (
                    <div key={userMsg.user_id || index} className={styles.userMessageItem}>
                      <div className={styles.userMessageHeader}>
                        <div className={styles.userInfo}>
                          <strong>{userMsg.username}</strong>
                          <span className={styles.userMeta}>
                            ({userMsg.wins}-{userMsg.losses}) - ${userMsg.amount_owed?.toFixed(2) || '0.00'}
                            {userMsg.llm_generated && <span className={styles.llmBadge}>LLM</span>}
                            {!userMsg.llm_generated && <span className={styles.defaultBadge}>Default</span>}
                          </span>
                        </div>
                        <button
                          className={styles.regenerateButton}
                          onClick={() => handleRegenerateUser(userMsg.user_id)}
                          disabled={regeneratingUserId === userMsg.user_id}
                        >
                          {regeneratingUserId === userMsg.user_id ? '🔄...' : '🔄 Regenerate'}
                        </button>
                      </div>
                      <div className={styles.messageBox}>
                        <pre className={styles.messageText}>{userMsg.message}</pre>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            className={styles.sendButton}
            onClick={handleSend}
            disabled={!previewData || sending || loading}
          >
            {sending ? 'Sending...' : `📧 Send ${previewData?.userMessages?.length || 0} Message(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

PaymentMessageReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  season: PropTypes.number.isRequired,
  useLLM: PropTypes.bool.isRequired,
  onSendComplete: PropTypes.func
};

export default PaymentMessageReviewModal;
